import { useEffect, useState } from "react";
import { apiJson } from "./api";

/**
 * stats — Rev 25.6 Loop F.
 *
 * Public + admin clients for the new /v1/(public|admin)/stats
 * endpoints. The public endpoint is unauthenticated + edge-cached
 * 5 min; the admin endpoint writes the configurable projects count.
 */

export type Stats = {
  clients: number;
  projects: number;
  uptime: string;
};

/**
 * Public read. Returns the headline stats for HomeProofStrip +
 * ProofPanel. Falls back to mocked numbers if the endpoint is
 * unreachable (matches Phase 2 ProofPanel mock so the page still
 * reads as on-brand during a brief outage).
 *
 * Rev 25.6.3 — cache-busted via a per-request timestamp parameter so
 * the browser never returns a stale {clients:0,...} from disk cache
 * after an admin Settings save. The server-side response already
 * sends `must-revalidate` + s-maxage=30, but adding a cb=<ts> param
 * defeats any intermediate cache that ignores the directive.
 */
export async function fetchStats(): Promise<Stats> {
  try {
    return await apiJson<Stats>(`/v1/public/stats?cb=${Date.now()}`, { anonymous: true });
  } catch {
    return { clients: 0, projects: 0, uptime: "99.98%" };
  }
}

const FALLBACK: Stats = { clients: 0, projects: 0, uptime: "99.98%" };

// One in-flight request per page load, shared by every consumer
// (HomeProofStrip, MegaMenu) so the nav and the proof strip can never
// show different numbers and the endpoint is hit once, not twice.
let shared: Promise<Stats> | null = null;
let resolved: Stats | null = null;

/**
 * Shared hook. `loaded` flips true once the fetch settles (success or
 * fallback) so callers can render a neutral placeholder instead of a
 * fabricated number while the request is in flight.
 */
export function useStats(): { stats: Stats; loaded: boolean } {
  const [stats, setStats] = useState<Stats>(resolved ?? FALLBACK);
  const [loaded, setLoaded] = useState(resolved !== null);
  useEffect(() => {
    if (resolved) return;
    let alive = true;
    shared ??= fetchStats().then((s) => (resolved = s));
    shared.then((s) => {
      if (!alive) return;
      setStats(s);
      setLoaded(true);
    });
    return () => { alive = false; };
  }, []);
  return { stats, loaded };
}

/**
 * Admin write. Patch either or both of the configurable counters.
 * Response carries the post-write canonical state plus the uptime
 * string (which is still hardcoded server-side).
 */
export async function updateAdminStats(patch: { clients?: number; projects?: number }): Promise<{ clients: number; projects: number; uptime: string }> {
  return apiJson<{ clients: number; projects: number; uptime: string }>("/v1/admin/stats", {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

/**
 * Admin read — used by the Settings panel to preload the current
 * configurable values for clients + projects. uptime is surfaced
 * via the public endpoint when needed.
 */
export async function fetchAdminStats(): Promise<{ clients: number; projects: number }> {
  return apiJson<{ clients: number; projects: number }>("/v1/admin/stats");
}
